import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import { usePosthogTracking } from '@/hooks/usePosthogTracking';
import { useToast } from '@/hooks/useToast';
import { LoadingPage } from '@/pages';
import { revokeHubspotCookies } from '@/utils/hubspot';
import {
  buildPostHogGlobalProperties,
  buildPostHogSnapshotProperties,
  PostHogCustomEvent,
} from '@/utils/posthog';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useNavigate } from 'react-router-dom';
import { Md5 } from 'ts-md5';

const CompleteSignIn: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const toast = useToast();
  const { trackPostHogEventWithAllowTracking } = usePosthogTracking();

  useEffect(() => {
    if (!auth.isAuthenticated) {
      auth.signinSilent().catch((error) => {
        Sentry.captureException(error, {
          tags: { oidc_operation: 'signinSilent' },
        });
      });
      return;
    }

    // Just in case revoke the cookies also here, Hubspot will reattach them automatically.
    revokeHubspotCookies();

    window.history.replaceState({}, document.title, window.location.pathname);

    const updateLastLoginDate = async () => {
      if (auth.user) {
        try {
          await client.default.updateLastLoginDate(auth.user);
          const user = await client.default.getMe();

          if (user?.account?.allowPosthogTracking) {
            trackPostHogEventWithAllowTracking(
              PostHogCustomEvent.USER_LOGGED_IN,
              {
                distinct_id: Md5.hashStr(user.id),
                ...buildPostHogGlobalProperties(user.account, user),
                $set: buildPostHogSnapshotProperties(user.account),
              },
            );
          }
        } catch (e) {
          handleAxiosError(e as AxiosError, toast);
        }
      }
    };

    updateLastLoginDate();

    navigate('/', {
      replace: true,
    });
  }, [auth.isAuthenticated]);

  return <LoadingPage message={t('auth.signIn.wait')} />;
};

export default CompleteSignIn;
